/**
 * Copyright 2024 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch} from 'redux';

import {getAllChannelsState} from 'services/channel.service';
import store from 'store';
import {IAppStore} from 'store/reducers';
import {IChannel} from 'interfaces/channelProps';
import {transformToPortalError} from 'utility/error-handler';

export const CHANNELS_PUBLISHING_STATE_FETCHING = 'CHANNELS_PUBLISHING_STATE_FETCHING' as const;
export const CHANNELS_PUBLISHING_STATE_DONE = 'CHANNELS_PUBLISHING_STATE_DONE' as const;
export const CHANNELS_PUBLISHING_STATE_ERROR = 'CHANNELS_PUBLISHING_STATE_ERROR' as const;

export const allChannelPublishingStateSelector = ({channelsPublishing}: IAppStore): {channelId: string; isOnline: boolean; multipleStreams: boolean}[] =>
  channelsPublishing.publishingState;
export const channelPublishingStateSelector = ({channelsPublishing}: IAppStore, channelId: string): number =>
  channelsPublishing.publishingState[channelId];

interface IRequestChannelsPublishingAction {
  type: typeof CHANNELS_PUBLISHING_STATE_FETCHING;
  payload?: string[];
}
interface IReceiveChannelsPublishingAction {
  type: typeof CHANNELS_PUBLISHING_STATE_DONE;
  payload: {channelId: string; isOnline: boolean; multipleStreams: boolean}[];
}
interface IFailedChannelsPublishingAction {
  type: typeof CHANNELS_PUBLISHING_STATE_ERROR;
  error: null | string;
}
export type FetchingChannelsPublishingActions =
  | IRequestChannelsPublishingAction
  | IReceiveChannelsPublishingAction
  | IFailedChannelsPublishingAction;
interface IGetChannelsPublishingActions {
  request: (payload?: string[]) => IRequestChannelsPublishingAction;
  receive: (payload: {channelId: string; isOnline: boolean; multipleStreams: boolean}[]) => IReceiveChannelsPublishingAction;
  failed: (error: null | string) => IFailedChannelsPublishingAction;
}
export const getChannelsPublishingActions: IGetChannelsPublishingActions = {
  request: payload => ({
    type: CHANNELS_PUBLISHING_STATE_FETCHING,
    payload
  }),
  receive: payload => ({
    type: CHANNELS_PUBLISHING_STATE_DONE,
    payload
  }),
  failed: error => ({
    type: CHANNELS_PUBLISHING_STATE_ERROR,
    error
  })
};
export const fetchChannelsPublishingState = (propChannelsIdOnDisplay?: string[]) => async(
  dispatch: Dispatch<FetchingChannelsPublishingActions>
): Promise<void> => {
  const state = store.getState();
  const channelList: IChannel[] = state.channels?.channelList;
  const {receive, request, failed} = getChannelsPublishingActions;

  dispatch(request());

  const channelsIdOnDisplay: string[] = propChannelsIdOnDisplay || channelList.map(val => val.channelId);

  try {
    const response = await getAllChannelsState(channelsIdOnDisplay);

    dispatch(receive(response));
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError.message));

    throw portalError;
  }
};

export type ChannelsPublishingActions = FetchingChannelsPublishingActions;